;(function (angular) {
   'use strict'

   angular.module('internalWebsiteApp').factory('logisticsData', logisticsData)

   function logisticsData(AzureAPI, $http, $q, ENV, config, util) {
      //================================================================================
      // Current user
      //================================================================================
      function getCurrentUser() {
         var options = {
            inline: 'email',
         }
         return util.asyncRetryOnce(AzureAPI.person.get, options).then(util.cleanItem)
      }

      //================================================================================
      // Routes
      //================================================================================

      function getAllRoutes() {
         function getRoutes(start, limit) {
            return AzureAPI.route.query({start: start, limit: limit}).$promise
         }
         return util.getPagesUntilEnd(getRoutes, 0, config.apiLimit)
      }

      function updateRoute(route) {
         return util.asyncRetryOnce(AzureAPI.route.save, route)
      }

      //================================================================================
      // Trips
      //================================================================================

      function getTrips(options) {
         var defaultOptions = {
            limit: config.apiLimit,
         }
         var requestOptions = angular.extend({}, defaultOptions, options)
         return util.asyncRetryOnce(AzureAPI.trip.query, requestOptions)
      }

      function getFurthestOutTrip(routeName) {
         return getTrips({
            route: routeName,
            limit: 1,
         }).then(util.first)
      }

      function getRoutesEarliestFutureTrip(routeName) {
         var options = {
            route: routeName,
            'cutoff-after': new Date(),
            confirmed: false,
            start: -1,
            limit: 1,
         }
         return getTrips(options)
            .then(util.cleanItem)
            .then(function (trips) {
               return trips[0]
            })
      }

      function updateTrip(trip) {
         return util.asyncRetryOnce(AzureAPI.trip.save, trip)
      }

      function deleteTrip(tripId) {
         return util.asyncRetryOnce(AzureAPI.trip.delete, {
            id: tripId,
         })
      }

      function createTrip(trip) {
         return util.asyncRetryOnce(AzureAPI.trip.create, trip)
      }

      //================================================================================
      // Truckloads
      //================================================================================

      function getTruckloadById(truckloadId) {
         return util.asyncRetryOnce(AzureAPI.truckload.get, {id: truckloadId}).then(util.cleanItem)
      }

      function updateTruckload(truckload) {
         return util.asyncRetryOnce(AzureAPI.truckload.save, truckload)
      }

      function getTruckloadEndpoint(truckloadId, maybePathSuffix) {
         return ENV.apiEndpoint + '/truckload/' + truckloadId + (maybePathSuffix || '')
      }

      function requestTruckloadEndpoint(method, truckloadId, maybePathSuffix, maybeData) {
         var requestConfig = {
            method: method,
            url: getTruckloadEndpoint(truckloadId, maybePathSuffix),
            withCredentials: true,
         }

         if (maybeData !== undefined) {
            requestConfig.data = maybeData
         }

         return $http(requestConfig).then(function (response) {
            return response.data
         })
      }

      function createContractorPo(truckloadId) {
         return requestTruckloadEndpoint('POST', truckloadId, '/generate-po/contractor')
      }

      function createShuttlePo(truckloadId) {
         return requestTruckloadEndpoint('POST', truckloadId, '/generate-po/shuttle')
      }

      function patchTruckloadOdometer(truckloadId, payload) {
         return requestTruckloadEndpoint('PATCH', truckloadId, '', payload)
      }

      function getOdometerAudit(truckloadId) {
         return requestTruckloadEndpoint('GET', truckloadId, '/odometer-audit')
      }

      //================================================================================
      // Trip Stops
      //================================================================================

      function getAllTripStops(tripId) {
         function getTripStops(start, limit) {
            return AzureAPI.stop.query({
               trip: tripId,
               start: start,
               limit: limit,
               inline: 'truckload',
            }).$promise
         }
         return util.getPagesUntilEnd(getTripStops, 0, config.apiLimit)
      }

      function getTripsEarliestDrop(tripId) {
         // returns a trip's earliest stop that's a drop (not a pickup)
         function getTripStop(start) {
            return AzureAPI.stop.query({
               trip: tripId,
               start: start,
               limit: 1,
            }).$promise
         }

         var start = 0
         function retryUntilDrop() {
            return getTripStop(start).then(function (stops) {
               if (!stops[0]) {
                  return
               }
               if (stops[0].dropId) {
                  return stops[0]
               }
               // If the stop doesn't have a `dropId` then it's a pickup.
               // Retry until we get the earliest drop.
               start++
               return retryUntilDrop()
            })
         }

         return retryUntilDrop()
      }

      function deleteStop(options) {
         return util.asyncRetryOnce(AzureAPI.stop.delete, options)
      }

      function createStop(options, data) {
         return util.asyncRetryOnce(AzureAPI.stop.create, options, data)
      }

      function updateStop(options, data) {
         return util.asyncRetryOnce(AzureAPI.stop.update, options, data)
      }

      //================================================================================
      // Drops
      //================================================================================

      function getDropById(dropId) {
         return util
            .asyncRetryOnce(AzureAPI.drop.get, {id: dropId, homeDeliveryDriverPreferences: true})
            .then(util.cleanItem)
      }

      function getAllRouteDrops(route, options) {
         function getDrops(start, limit) {
            var defaultOptions = {
               route: route.name,
               limit: limit,
               start: start,
               homeDeliveryDriverPreferences: true,
            }
            var requestOptions = angular.extend({}, defaultOptions, options)
            return AzureAPI.drop.query(requestOptions).$promise
         }
         return util.getPagesUntilEnd(getDrops, 0, config.apiLimit)
      }

      function getAllRouteDropsActive(route) {
         return $q
            .all([
               getAllRouteDrops(route, {active: true}),
               util.asyncRetryOnce(getRouteDropExclusions, route.id).catch(function () {
                  // If exclusions cannot be loaded, fall back to none rather than
                  // blocking the route's drops from loading
                  return {}
               }),
            ])
            .then(function (results) {
               var drops = results[0]
               var exclusionsByDropId = results[1]
               drops.forEach(function (drop) {
                  drop.deliveryExclusionRanges = exclusionsByDropId[drop.id] || []
               })
               return drops
            })
      }

      function getAllRouteDropsInactive(route) {
         return getAllRouteDrops(route, {active: false})
      }

      function getDropHierarchy(cutoff) {
         if (cutoff === undefined) {
            const now = new Date()
            cutoff = new Date(now.setDate(now.getDate() - 1))
         }
         return $http({
            method: 'GET',
            url: ENV.apiEndpoint + '/v2/drops/drop-hierarchy',
            withCredentials: true,
            params: {cutoff_after: cutoff.toISOString()},
         })
      }

      /**
       * Retrieves exclusions for every drop point associated with a route
       *
       * @description Fetches all drop point exclusions for a route in a single request
       * (instead of one request per drop point) and groups them by drop point id.
       * Rows keep the API ordering (day of week, then start time).
       *
       * @param {string|number} routeId - The unique identifier for the route
       * @returns {Promise<Object>} A promise that resolves to a map of drop point id to
       *                            an array of exclusion objects. Drop points without
       *                            exclusions are absent from the map. Rejects on request
       *                            failure so callers can retry or supply a fallback.
       *
       * @example
       * // Get exclusions for all drop points on route 42
       * getRouteDropExclusions(42).then(function(exclusionsByDropId) {
       *    console.log('Exclusions for drop 123:', exclusionsByDropId[123]);
       * });
       *
       * @since Added in support for drop exclusions feature
       */
      function getRouteDropExclusions(routeId) {
         return $http({
            method: 'GET',
            url: ENV.apiEndpoint + '/v2/drops/drop_point_exclusion?route=' + routeId,
            withCredentials: true,
         }).then(function (response) {
            var exclusionsByDropId = {}
            response.data.forEach(function (exclusion) {
               var dropId = exclusion.dropPoint
               if (!exclusionsByDropId[dropId]) {
                  exclusionsByDropId[dropId] = []
               }
               exclusionsByDropId[dropId].push(exclusion)
            })
            return exclusionsByDropId
         })
      }

      function moveOrders(oldTripId, oldDropId, newDropPointTripId) {
         return $http({
            method: 'POST',
            url: ENV.apiEndpoint + '/v2/drops/bulk-move-orders',
            withCredentials: true,
            isArray: false,
            data: {
               fromDropId: oldDropId,
               fromTripId: oldTripId,
               toDropPointTripId: newDropPointTripId,
            },
         })
      }

      //================================================================================
      // Pickups
      //================================================================================

      function getAllRoutePickups(route) {
         function getPickups(start, limit) {
            return AzureAPI.pickup.query({
               route: route.name,
               start: start,
               limit: limit,
            }).$promise
         }
         return util.getPagesUntilEnd(getPickups, 0, config.apiLimit)
      }

      //================================================================================
      // People (e.g. Truck Drivers, Carrier Contractors, etc.)
      //================================================================================

      function getPerson(customerId) {
         if (customerId) {
            return AzureAPI.person.get({
               id: customerId,
            }).$promise
         } else {
            return new Promise((resolve, reject) => reject('Must pass a valid person id to getPerson.'))
         }
      }

      function getTruckloadRelatedPeople() {
         return AzureAPI.person
            .query({
               limit: config.apiLimit,
               customerType: 'truck-driver,carrier-contractor,carrier-shuttle',
            })
            .$promise.then(util.cleanItem)
            .then(function (people) {
               return util.sortByProperty('name', people)
            })
      }

      //================================================================================

      return {
         getCurrentUser: getCurrentUser,
         // People (e.g. Truck Drivers, etc.)
         getPerson: getPerson,
         getTruckloadRelatedPeople: getTruckloadRelatedPeople,
         // Routes
         getAllRoutes: getAllRoutes,
         updateRoute: updateRoute,
         // Trips
         getTrips: getTrips,
         getFurthestOutTrip: getFurthestOutTrip,
         getRoutesEarliestFutureTrip: getRoutesEarliestFutureTrip,
         updateTrip: updateTrip,
         deleteTrip: deleteTrip,
         createTrip: createTrip,
         // Truckloads
         getTruckloadById: getTruckloadById,
         updateTruckload: updateTruckload,
         createContractorPo: createContractorPo,
         createShuttlePo: createShuttlePo,
         patchTruckloadOdometer: patchTruckloadOdometer,
         getOdometerAudit: getOdometerAudit,
         // Trip Stops
         getAllTripStops: getAllTripStops,
         getTripsEarliestDrop: getTripsEarliestDrop,
         deleteStop: deleteStop,
         createStop: createStop,
         updateStop: updateStop,
         // Drops
         getDropById: getDropById,
         getAllRouteDrops: getAllRouteDrops,
         getAllRouteDropsActive: getAllRouteDropsActive,
         getAllRouteDropsInactive: getAllRouteDropsInactive,
         moveOrders: moveOrders,
         // Pickups
         getAllRoutePickups: getAllRoutePickups,
         getDropHierarchy: getDropHierarchy,
      }
   }
})(angular)
